import Big from 'big.js'
import { findByChainId } from 'chain-list'
import { useChainId } from 'metronome-ui/hooks/chains'
import { fromUnit } from 'metronome-utils'
import useTranslation from 'next-translate/useTranslation'
import { useContext, useMemo } from 'react'

import useRefreshedUsdRate from '../hooks/useRefreshedUsdRate'
import { uiFiatDecimals } from '../utils'

import IconContainer from './icons/IconContainer'
import LiquidationConditions from './LiquidationConditions'
import { MetronomeContext } from './MetronomeContext'
import OperationButton from './OperationButton'
import {
  OperationDetailsItem,
  OperationDetailsList,
  OperationDetailsPreviewItem
} from './OperationDetails'

// eslint-disable-next-line complexity
const PositionSummary = function ({
  amountIsZero,
  borrowed = 0,
  collateralTotalSupplyExceeded,
  crossChainEnabled,
  debtTokenTotalSupplyExceeded,
  displayedNativeFee,
  estimatedAPY,
  leverage = 1,
  leveragedAmount,
  selectedSynthetic,
  syntheticTotalSupplyExceeded,
  validatingNativeFee,
  walletIsActive
}) {
  const { t } = useTranslation()
  const chainId = useChainId()
  const { decimals: nativeTokenDecimals, nativeTokenSymbol } =
    findByChainId(chainId)

  const {
    debtPosition: { debtInUsd = 0, depositInUsd = '0', issuableLimitInUsd = 0 },
    inputValue: collateralInputValue,
    metronome,
    operationExecuted,
    selectedTokenUsdRate: selectedCollateralUsdRate,
    selectedToken: selectedCollateral
  } = useContext(MetronomeContext)

  const [selectedSynthUsdRate] = useRefreshedUsdRate({
    metronome,
    token: selectedSynthetic
  })

  const totalDepositedNextAmount =
    selectedCollateralUsdRate && selectedCollateral
      ? leveragedAmount.mul(selectedCollateralUsdRate).plus(depositInUsd)
      : Big(0)

  const currentDebtInUsd = Big(debtInUsd)

  const nextDebtInUsd =
    selectedSynthUsdRate && borrowed
      ? currentDebtInUsd.plus(Big(borrowed).times(selectedSynthUsdRate))
      : Big(debtInUsd)

  const isValid = useMemo(
    () => !!(collateralInputValue && borrowed && Big(borrowed).gt(0)),
    [borrowed, collateralInputValue]
  )

  const liquidationPrice = useMemo(
    function () {
      if (
        borrowed &&
        collateralInputValue &&
        leverage &&
        selectedSynthUsdRate &&
        selectedCollateral &&
        selectedCollateralUsdRate
      ) {
        // add the borrowed synth to the current position's debt
        const newDebtInUsd = Big(debtInUsd).plus(
          Big(borrowed).times(selectedSynthUsdRate)
        )

        // Add the issuableLimit of the new leveraged deposit to the current position's issuableLimit
        // issuableLimitInUsd + issuableLimitInUsdOfNewDeposit
        const newIssuableLimitInUsd = Big(issuableLimitInUsd).plus(
          Big(collateralInputValue)
            .times(leverage)
            .times(selectedCollateralUsdRate)
            .times(selectedCollateral.collateralFactor)
            .div(100)
        )

        if (newIssuableLimitInUsd.gt(0)) {
          // Formula for liquidation price, based on new expected values of debt and issuableLimit
          // newDebtInUsd * currentPriceOfDepositToken / newIssuableLimitInUsd
          return newDebtInUsd
            .times(selectedCollateralUsdRate)
            .div(newIssuableLimitInUsd)
            .round(2, Big.roundUp)
            .toNumber()
        }
      }
      return undefined
    },
    [
      borrowed,
      collateralInputValue,
      debtInUsd,
      issuableLimitInUsd,
      leverage,
      selectedCollateral,
      selectedCollateralUsdRate,
      selectedSynthUsdRate
    ]
  )

  return (
    <div className="bg-block mt-4 h-full w-full space-y-4 self-start rounded-md p-6 text-white md:sticky md:top-20 md:m-0 md:w-1/2">
      <span className="text-lg font-medium">{t('summary')}</span>
      <div className="flex space-x-4 text-center">
        <div className="bg-blur flex w-1/2 flex-col px-5 py-3">
          <span className="text-gray-secondary">{t('estimated-apy')}</span>
          {estimatedAPY ? (
            Big(estimatedAPY).lt(0) ? (
              <span className="text-red-600">
                {Big(estimatedAPY).toFixed(2)}%
              </span>
            ) : (
              <>{Big(estimatedAPY).toFixed(2)}%</>
            )
          ) : (
            '-'
          )}
        </div>
        <div className="bg-blur flex w-1/2 flex-col px-5 py-3">
          <span className="text-gray-secondary">{t('your-loop')}</span>
          {Big(leverage).toFixed(2)}x
        </div>
      </div>
      <div className="bg-blue-secondary rounded-lg">
        <OperationDetailsList>
          <OperationDetailsPreviewItem
            currentValue={`$${Big(depositInUsd).toFixed(2)}`}
            nextValue={`$${totalDepositedNextAmount.toFixed(2)}`}
            title={t('total-deposited')}
          />
          <OperationDetailsPreviewItem
            currentValue={`$${currentDebtInUsd.toFixed(uiFiatDecimals)}`}
            nextValue={`${leverage > 1 ? '≈' : ''}$${nextDebtInUsd.toFixed(
              uiFiatDecimals
            )}`}
            title={t('total-synth-borrowed')}
          />
          <OperationDetailsItem title={t('synth-balance-fee')}>
            <div className="flex">
              {selectedSynthetic?.interestRate
                ? `${selectedSynthetic.interestRate}%`
                : '-'}
            </div>
          </OperationDetailsItem>
          {crossChainEnabled && (
            <OperationDetailsItem title={t('estimated-native-fee')}>
              <div>
                {!validatingNativeFee ? (
                  `${Big(
                    fromUnit(displayedNativeFee, nativeTokenDecimals)
                  ).toFixed(
                    parseInt(process.env.NEXT_PUBLIC_UI_CRYPTO_DECIMALS)
                  )}
                    ${nativeTokenSymbol}`
                ) : (
                  <IconContainer height="16" name="loading" width="16" />
                )}
              </div>
            </OperationDetailsItem>
          )}
        </OperationDetailsList>
      </div>
      {!!liquidationPrice && borrowed && (
        <LiquidationConditions
          collateral={selectedCollateral}
          currentPrice={selectedCollateralUsdRate?.round(2).toNumber()}
          liquidationPrice={liquidationPrice}
        />
      )}
      <OperationButton
        buttonType="submit"
        disabled={
          amountIsZero ||
          collateralTotalSupplyExceeded ||
          syntheticTotalSupplyExceeded ||
          debtTokenTotalSupplyExceeded ||
          !walletIsActive ||
          !isValid ||
          operationExecuted
        }
        loading={operationExecuted}
      >
        {t('confirm')}
      </OperationButton>
    </div>
  )
}

export default PositionSummary
